import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '../../components/ui/Modal';
import { useAppVersionTelemetry } from './useAppVersionTelemetry';
import { 
  downloadMarkdownReport, 
  downloadHtmlReport, 
  printAuditReport 
} from './exportAuditReport';
import { 
  ShieldCheck, 
  RefreshCw, 
  ExternalLink, 
  Trash2, 
  Smartphone, 
  Monitor, 
  CheckCircle2, 
  AlertTriangle,
  Layers,
  GitBranch,
  FileText,
  Code,
  Printer,
  Globe,
  Image as ImageIcon,
  MessageSquare,
  Tag,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Filter,
  X
} from 'lucide-react';
import { ALL_TRELLO_CARDS, type TrelloCardDetail } from './routesTrelloMap';
import { TesterSessionsPanel, isSessionRecorderEnabled } from './session-recorder';
import { Activity } from 'lucide-react';

interface AuditTelemetryModalProps {
  open: boolean;
  onClose: () => void;
  telemetry: ReturnType<typeof useAppVersionTelemetry>;
}

// Ordem estrita de grupos da esteira de produção (Pendências no topo para atendimento prioritário)
const LIST_SECTIONS_ORDER = [
  { key: 'pendencias', label: 'Pendências', icon: '⚠️', highlight: true },
  { key: 'retorno', label: 'Retorno', icon: '🔄' },
  { key: 'desenvolvimento', label: 'Desenvolvimento', icon: '🛠️' },
  { key: 'demanda', label: 'Demanda', icon: '📥' },
  { key: 'homologacao', label: 'Homologação', icon: '🧪' },
  { key: 'conclusao', label: 'Conclusão', icon: '✅' },
];

function normalizeListName(name: string): string {
  const norm = (name || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  if (norm.includes('conclu')) return 'conclusao';
  if (norm.includes('homolog')) return 'homologacao';
  if (norm.includes('desenvolv')) return 'desenvolvimento';
  if (norm.includes('retorno')) return 'retorno';
  if (norm.includes('pendenc')) return 'pendencias';
  if (norm.includes('demanda')) return 'demanda';
  return 'desenvolvimento';
}

function getListBadgeClass(listName: string): string {
  const key = normalizeListName(listName);
  return `ac-audit-list-badge--${key}`;
}

export const AuditTelemetryModal: React.FC<AuditTelemetryModalProps> = ({
  open,
  onClose,
  telemetry,
}) => {
  const {
    localManifest,
    remoteManifest,
    isStale,
    viewport,
    isAuditVisible,
    toggleAuditVisible,
    checkRemoteVersion,
    purgeAndReload,
    currentRouteMeta,
  } = telemetry;

  const [purging, setPurging] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  const [filterScope, setFilterScope] = useState<'route' | 'all'>('route'); // 'route' (padrão) ou 'all'
  const [activeModalTab, setActiveModalTab] = useState<'trello' | 'sessions'>('trello');
  // Apenas uma seção aberta por vez (Single Accordion)
  const [activeSection, setActiveSection] = useState<string | null>('desenvolvimento');

  const routeCards = currentRouteMeta.relatedTrelloCards || [];
  const currentRouteCardsSet = useMemo(() => new Set(routeCards.map((c) => c.id)), [routeCards]);

  // Lista base filtrada por escopo (apenas desta rota vs todos)
  const baseCards = useMemo(() => {
    if (filterScope === 'route' && routeCards.length > 0) {
      return routeCards;
    }
    return ALL_TRELLO_CARDS;
  }, [filterScope, routeCards]);

  // Agrupamento por lista normalizada
  const groupedCards = useMemo(() => {
    const groups: Record<string, TrelloCardDetail[]> = {
      demanda: [],
      desenvolvimento: [],
      retorno: [],
      pendencias: [],
      homologacao: [],
      conclusao: [],
    };

    baseCards.forEach((card) => {
      const key = normalizeListName(card.listName);
      if (!groups[key]) groups[key] = [];
      groups[key].push(card);
    });

    // Ordenação interna em cada grupo: cards com atividade mais recente no topo
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const lastDateA = a.comments.length > 0
          ? new Date(a.comments[a.comments.length - 1].date).getTime()
          : new Date(a.dateLastActivity || 0).getTime();

        const lastDateB = b.comments.length > 0
          ? new Date(b.comments[b.comments.length - 1].date).getTime()
          : new Date(b.dateLastActivity || 0).getTime();

        return lastDateB - lastDateA;
      });
    });

    return groups;
  }, [baseCards]);

  // Seleciona automaticamente o card prioritário ao abrir (Pendências > Rota mais recente > Geral)
  useEffect(() => {
    if (open) {
      // 1. Prioridade máxima: cards em 'Pendências'
      const pendenciasCards = groupedCards.pendencias || [];
      if (pendenciasCards.length > 0) {
        setSelectedCardId(pendenciasCards[0].id);
        setActiveSection('pendencias');
        return;
      }

      // 2. Se não houver pendências, pega o card mais recente da rota ativa
      if (routeCards.length > 0) {
        const sorted = [...routeCards].sort((a, b) => {
          const lastDateA = a.comments.length > 0
            ? new Date(a.comments[a.comments.length - 1].date).getTime()
            : new Date(a.dateLastActivity || 0).getTime();
          const lastDateB = b.comments.length > 0
            ? new Date(b.comments[b.comments.length - 1].date).getTime()
            : new Date(b.dateLastActivity || 0).getTime();
          return lastDateB - lastDateA;
        });

        const target = sorted[0];
        setSelectedCardId(target.id);
        const sectionKey = normalizeListName(target.listName);
        setActiveSection(sectionKey);
      } else if (ALL_TRELLO_CARDS.length > 0) {
        // 3. Fallback: primeiro card geral
        setSelectedCardId(ALL_TRELLO_CARDS[0].id);
        setActiveSection(normalizeListName(ALL_TRELLO_CARDS[0].listName));
      }
    }
  }, [open, currentRouteMeta, groupedCards, routeCards]);

  // Card selecionado ativo
  const activeCard: TrelloCardDetail | null = useMemo(() => {
    if (selectedCardId) {
      const found = ALL_TRELLO_CARDS.find((c) => c.id === selectedCardId);
      if (found) return found;
    }
    return baseCards[0] || ALL_TRELLO_CARDS[0] || null;
  }, [selectedCardId, baseCards]);

  // Abre a seção clicada e fecha todas as outras automaticamente
  const toggleSection = (key: string) => {
    setActiveSection((prev) => (prev === key ? null : key));
  };

  const handlePurge = async () => {
    setPurging(true);
    await purgeAndReload();
  };

  const isMobile = viewport.width < 768;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Auditoria & Assinatura de Homologação"
        size="full"
      >
        <div className="flex flex-col space-y-2">
          {/* Subtítulo & Rota Ativa */}
          <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-slate-200 text-xs text-slate-500">
            <span className="flex items-center gap-1.5 font-medium">
              <ShieldCheck className="text-blue-600" size={15} />
              Telemetria em tempo real, rastreabilidade Trello e diagnóstico de cache
            </span>
            <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-[0.70rem]">
              Rota Ativa: {currentRouteMeta.routePattern}
            </span>
          </div>

          {/* Banner de Alerta se versão estiver obsoleta */}
          {isStale && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-900 text-xs">
              <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={15} />
              <div>
                <strong>Atenção: Versão Obsoleta em Execução (Falso Positivo de Teste)</strong>
                <p className="mt-0.5 text-red-700">
                  O servidor possui a versão <strong>{remoteManifest?.version}</strong>, mas o navegador executa <strong>{localManifest.version}</strong>. Limpe os caches para revalidar.
                </p>
              </div>
            </div>
          )}

          {/* NAVEGAÇÃO DE ABAS: TRELLO VS SESSÕES DE TESTADORES */}
          <div className="flex items-center justify-between bg-slate-100 p-1 rounded-xl border border-slate-200">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveModalTab('trello')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeModalTab === 'trello'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                <Layers size={13} />
                Esteira Oficial Trello & Cards
              </button>
              <button
                onClick={() => setActiveModalTab('sessions')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeModalTab === 'sessions'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                <Activity size={13} />
                Sessões de Testadores & Replay
                <span className="bg-emerald-500 text-white text-[0.60rem] px-1.5 py-0.2 rounded-full font-extrabold uppercase">
                  Novo
                </span>
              </button>
            </div>

            <span className="text-[0.70rem] text-slate-500 hidden md:inline px-2 font-medium">
              {activeModalTab === 'trello' ? 'Homologação Rastreável por Rota' : 'Inspeção de Navegação, Cliques e Replay dos Testadores'}
            </span>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* CONTEÚDO CONDICIONAL DA ABA ATIVA */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {activeModalTab === 'sessions' ? (
            <div className="rounded-xl overflow-hidden border border-slate-700 h-[640px] shadow-inner">
              <TesterSessionsPanel currentVersion={localManifest.version} />
            </div>
          ) : (
            <div className="ac-audit-quad-layout">
            
            {/* ─── COLUNA 1: TELEMETRIA, METADADOS & AÇÕES (FIXO) ─── */}
            <div className="ac-audit-col-telemetry">
              <div className="space-y-2">
                {/* Build Local */}
                <div className="ac-audit-meta-card">
                  <div className="ac-audit-meta-header">
                    <Layers size={12} className="text-blue-600" />
                    Build Local
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Versão:</span>
                    <span className="ac-audit-val text-blue-700">{localManifest.version}</span>
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Commit:</span>
                    <span className="ac-audit-val font-mono">{localManifest.gitCommit}</span>
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Branch:</span>
                    <span className="ac-audit-val font-mono flex items-center gap-0.5">
                      <GitBranch size={10} /> {localManifest.gitBranch}
                    </span>
                  </div>
                </div>

                {/* Ambiente & Viewport */}
                <div className="ac-audit-meta-card">
                  <div className="ac-audit-meta-header">
                    <Monitor size={12} className="text-purple-600" />
                    Ambiente Testador
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Viewport:</span>
                    <span className="ac-audit-val font-mono text-[0.68rem]">{viewport.width}x{viewport.height}</span>
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Modo:</span>
                    <span className="ac-audit-val flex items-center gap-1">
                      {isMobile ? <Smartphone size={10} /> : <Monitor size={10} />}
                      {isMobile ? 'Mobile' : 'Desktop'}
                    </span>
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Cache:</span>
                    <span className="ac-audit-val">
                      {isStale ? (
                        <span className="text-red-600 font-bold text-[0.65rem]">⚠️ Obsoleto</span>
                      ) : (
                        <span className="text-emerald-600 font-bold text-[0.65rem]">🟢 Sinc</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Telemetria de Sessão & Replay */}
                <div className="ac-audit-meta-card">
                  <div className="ac-audit-meta-header">
                    <Activity size={12} className="text-emerald-600" />
                    Telemetria de Sessão
                  </div>
                  <div className="ac-audit-row">
                    <span className="ac-audit-label">Status:</span>
                    <span className="ac-audit-val text-emerald-700 font-bold text-[0.68rem]">
                      {isSessionRecorderEnabled() ? '🟢 Gravação Ativa' : '⚪ Pausada'}
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveModalTab('sessions')}
                    className="w-full mt-1.5 py-1 px-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-[0.68rem] font-bold flex items-center justify-center gap-1 transition-colors cursor-pointer"
                  >
                    <Activity size={11} />
                    Ver Sessões & Replay
                  </button>
                </div>

                {/* Ações de Exportação */}
                <div className="ac-audit-meta-card space-y-1.5">
                  <span className="text-[0.65rem] font-bold text-slate-700 uppercase tracking-wider block">
                    Exportar Homologação:
                  </span>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => downloadMarkdownReport(currentRouteMeta, localManifest, remoteManifest, viewport)}
                      className="p-1.5 text-[0.68rem] font-bold rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center gap-1 transition-colors"
                      title="Baixar Relatório em Markdown"
                    >
                      <FileText size={11} className="text-emerald-600" /> .MD
                    </button>
                    <button
                      onClick={() => downloadHtmlReport(currentRouteMeta, localManifest, remoteManifest, viewport)}
                      className="p-1.5 text-[0.68rem] font-bold rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center gap-1 transition-colors"
                      title="Baixar Relatório em HTML"
                    >
                      <Code size={11} className="text-blue-600" /> .HTML
                    </button>
                    <button
                      onClick={() => printAuditReport(currentRouteMeta, localManifest, remoteManifest, viewport)}
                      className="p-1.5 text-[0.68rem] font-bold rounded bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-1 transition-colors"
                      title="Imprimir ou Salvar em PDF"
                    >
                      <Printer size={11} /> PDF
                    </button>
                    <a
                      href={`/audit-report?route=${encodeURIComponent(currentRouteMeta.routePattern)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-[0.68rem] font-bold rounded bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-1 transition-colors text-center"
                      title="Abrir Portal Público de Homologação"
                    >
                      <Globe size={11} /> Portal
                    </a>
                  </div>
                </div>

                {/* Dossiês Executivos Trello PDF */}
                <div className="ac-audit-meta-card bg-slate-900 text-white border-slate-700 p-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] font-extrabold uppercase tracking-wider text-sky-400 flex items-center gap-1">
                      <FileText size={10} /> Dossiês Trello (PDF)
                    </span>
                    <span className="bg-sky-500/20 text-sky-300 text-[0.58rem] px-1 py-0.2 rounded font-mono font-bold">
                      83 cards
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-1 pt-0.5">
                    <a
                      href="/docs/trello/RELATORIO_EXECUTIVO_TRELLO_PREMIUM.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 bg-sky-600 hover:bg-sky-500 text-white rounded text-[0.62rem] font-bold text-center flex items-center justify-center gap-0.5 transition-colors"
                      title="1º PDF: Métricas, Gráficos e Taxonomia"
                    >
                      📊 PDF Métricas
                    </a>
                    <a
                      href="/docs/trello/DOSSIE_VISUAL_CARDS_TRELLO_PREMIUM.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 bg-purple-600 hover:bg-purple-500 text-white rounded text-[0.62rem] font-bold text-center flex items-center justify-center gap-0.5 transition-colors"
                      title="2º PDF: Dossiê Visual com Telas em Largura Total"
                    >
                      🖼️ PDF Visual
                    </a>
                  </div>
                </div>
              </div>

              {/* Botão de Purge e Toggle Rodapé */}
              <div className="space-y-1.5 pt-2 border-t border-slate-200">
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-[0.66rem] font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={isAuditVisible}
                    onChange={(e) => toggleAuditVisible(e.target.checked)}
                    className="rounded text-blue-600"
                  />
                  Exibir faixa no rodapé
                </label>

                <button
                  onClick={() => void checkRemoteVersion()}
                  className="w-full text-left text-blue-600 hover:text-blue-800 text-[0.66rem] font-semibold flex items-center gap-1 py-0.5"
                >
                  <RefreshCw size={10} /> Rechecar Servidor CDN
                </button>

                <button
                  onClick={handlePurge}
                  disabled={purging}
                  className="ac-audit-purge-btn"
                >
                  <Trash2 size={13} />
                  {purging ? 'Limpando...' : 'Limpar Caches & Storage'}
                </button>
              </div>
            </div>

            {/* ─── COLUNA 2: QUADRO TRELLO COM ACCORDION (SCROLL INDEPENDENTE) ─── */}
            <div className="ac-audit-col-cards">
              <div className="ac-audit-col-cards-sticky-header">
                {/* Seletor de Escopo: Desta Rota vs Todos os Cards */}
                <div className="flex items-center gap-1 p-0.5 bg-slate-200 rounded-lg text-[0.62rem] font-bold">
                  <button
                    onClick={() => setFilterScope('route')}
                    className={`flex-1 py-1 px-1.5 rounded-md transition-all flex items-center justify-center gap-1 ${
                      filterScope === 'route'
                        ? 'bg-white text-purple-900 shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Filter size={10} />
                    Desta Rota ({routeCards.length})
                  </button>
                  <button
                    onClick={() => setFilterScope('all')}
                    className={`flex-1 py-1 px-1.5 rounded-md transition-all flex items-center justify-center gap-1 ${
                      filterScope === 'all'
                        ? 'bg-white text-purple-900 shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Globe size={10} />
                    Todos ({ALL_TRELLO_CARDS.length})
                  </button>
                </div>
              </div>

              {LIST_SECTIONS_ORDER.map((sec) => {
                const cards = groupedCards[sec.key] || [];
                const isOpen = activeSection === sec.key;

                return (
                  <div 
                    key={sec.key} 
                    className={`ac-audit-accordion-group ${isOpen ? 'ac-audit-accordion-group--active' : ''}`}
                  >
                    <div
                      className={`ac-audit-accordion-header ${isOpen ? 'ac-audit-accordion-header--active' : ''}`}
                      onClick={() => toggleSection(sec.key)}
                    >
                      <span className="flex items-center gap-1.5">
                        <span>{sec.icon}</span>
                        <span>{sec.label}</span>
                        <span className={`text-[0.60rem] px-1.5 py-0.2 rounded-full font-mono ${cards.length > 0 ? (isOpen ? 'bg-purple-200 text-purple-950 font-bold' : 'bg-slate-200/80 text-slate-800') : 'bg-slate-100 text-slate-400'}`}>
                          {cards.length}
                        </span>
                      </span>
                      {isOpen ? <ChevronDown size={13} className="text-purple-700" /> : <ChevronRight size={13} className="text-slate-400" />}
                    </div>

                    {isOpen && (
                      <div className="ac-audit-accordion-content">
                        {cards.length === 0 ? (
                          <p className="text-[0.64rem] text-slate-400 italic p-2 text-center bg-slate-50 rounded-lg">
                            Nenhum card em {sec.label} no momento
                          </p>
                        ) : (
                          cards.map((c) => {
                            const isSelected = activeCard?.id === c.id;
                            const isFromRoute = currentRouteCardsSet.has(c.id);
                            const isPendencia = normalizeListName(c.listName) === 'pendencias';
                            const lastActivity = c.comments.length > 0
                              ? c.comments[c.comments.length - 1].date
                              : c.dateLastActivity;

                            return (
                              <div
                                key={c.id}
                                onClick={() => setSelectedCardId(c.id)}
                                className={`ac-audit-card-item ${isPendencia ? 'border-amber-300 bg-amber-50/30' : ''} ${isSelected ? 'ac-audit-card-item--active' : ''}`}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  {isPendencia ? (
                                    <span 
                                      className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-amber-100 border border-amber-300 text-amber-900 font-extrabold text-[0.58rem] shadow-2xs" 
                                      title="PENDÊNCIAS: Este card possui pendências que precisam de atendimento imediato"
                                    >
                                      <AlertTriangle size={9} className="text-amber-600 animate-pulse" />
                                      PENDÊNCIAS
                                    </span>
                                  ) : (
                                    <span className={`ac-audit-list-badge ${getListBadgeClass(c.listName)}`}>
                                      {c.listName}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-1">
                                    {isPendencia && (
                                      <span 
                                        className="text-[0.52rem] bg-yellow-400 text-slate-950 font-black px-1 rounded flex items-center gap-0.5 border border-yellow-500 shadow-2xs"
                                        title="PENDÊNCIAS"
                                      >
                                        <AlertTriangle size={8} /> ATENÇÃO
                                      </span>
                                    )}
                                    {isFromRoute && (
                                      <span className="text-[0.55rem] bg-amber-100 text-amber-900 font-black px-1 rounded flex items-center gap-0.5 border border-amber-300">
                                        <Sparkles size={8} /> ROTA
                                      </span>
                                    )}
                                    <span className="text-[0.58rem] font-mono text-purple-700 font-bold">
                                      {c.commentsCount} 💬
                                    </span>
                                  </div>
                                </div>

                                <span className="ac-audit-card-item-title flex items-start gap-1">
                                  {isPendencia && (
                                    <span title="PENDÊNCIAS">
                                      <AlertTriangle size={11} className="text-amber-600 shrink-0 mt-0.5" />
                                    </span>
                                  )}
                                  <span>{c.name}</span>
                                </span>

                                {c.labels.length > 0 && (
                                  <div className="ac-audit-labels-row">
                                    {c.labels.map((l, idx) => (
                                      <span key={idx} className="ac-audit-label-tag">
                                        {l.name}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center justify-between text-[0.56rem] text-slate-400 font-mono mt-0.5">
                                  <span>
                                    {new Date(lastActivity).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ─── COLUNA 3: FICHA DO CARD SELECIONADO & ANEXOS (SCROLL INDEPENDENTE) ─── */}
            <div className="ac-audit-col-details">
              {activeCard ? (
                <>
                  {/* Banner de Aviso Especial para Cards em Pendências */}
                  {normalizeListName(activeCard.listName) === 'pendencias' && (
                    <div 
                      className="flex items-center gap-2.5 p-2.5 bg-amber-50 border-2 border-amber-400 rounded-xl text-amber-950 text-xs shadow-xs"
                      title="PENDÊNCIAS: Este cartão possui itens pendentes que requerem atendimento imediato pela equipe."
                    >
                      <div className="p-1.5 bg-amber-200/80 rounded-lg text-amber-800 shrink-0">
                        <AlertTriangle size={20} className="text-amber-700 animate-bounce" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 font-black uppercase text-[0.72rem] tracking-wider text-amber-900">
                          <span>⚠️ QUADRO DE PENDÊNCIAS</span>
                          <span className="bg-amber-300 text-amber-950 text-[0.58rem] px-1.5 py-0.2 rounded font-extrabold">
                            Prioridade de Atendimento
                          </span>
                        </div>
                        <p className="text-[0.66rem] text-amber-900/90 font-medium mt-0.5">
                          Este card está no quadro de <strong>Pendências</strong> do Trello. Verifique os comentários e anexos abaixo para atender às solicitações antes de avançar para Homologação.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-2">
                    <span className="ac-audit-card-title-lg flex items-start gap-1.5">
                      {normalizeListName(activeCard.listName) === 'pendencias' && (
                        <span title="PENDÊNCIAS">
                          <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                        </span>
                      )}
                      <span>{activeCard.name}</span>
                    </span>
                    <a
                      href={activeCard.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[0.66rem] bg-purple-100 hover:bg-purple-200 text-purple-900 font-bold px-2 py-1 rounded-lg shrink-0 inline-flex items-center gap-1 transition-colors"
                      title="Abrir no Trello"
                    >
                      Trello <ExternalLink size={10} />
                    </a>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    {normalizeListName(activeCard.listName) === 'pendencias' ? (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 border border-amber-400 text-amber-950 font-extrabold text-[0.65rem] shadow-xs" 
                        title="PENDÊNCIAS: Este card requer atendimento prioritário"
                      >
                        <AlertTriangle size={11} className="text-amber-600" />
                        PENDÊNCIAS
                      </span>
                    ) : (
                      <span className={`ac-audit-list-badge ${getListBadgeClass(activeCard.listName)}`}>
                        {activeCard.listName}
                      </span>
                    )}
                    <span className="text-[0.66rem] text-slate-500 font-mono">
                      Última Atividade: {new Date(activeCard.dateLastActivity).toLocaleString('pt-BR')}
                    </span>
                  </div>

                  {activeCard.labels.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {activeCard.labels.map((l, idx) => (
                        <span key={idx} className="text-[0.62rem] px-2 py-0.5 rounded bg-white border border-purple-200 text-purple-900 font-semibold flex items-center gap-1">
                          <Tag size={9} className="text-purple-600" />
                          {l.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {activeCard.desc && (
                    <div className="space-y-1">
                      <span className="text-[0.66rem] font-bold text-purple-950 uppercase tracking-wider block">
                        Decisão Arquitetural / Comportamento:
                      </span>
                      <div className="ac-audit-card-desc-box">
                        {activeCard.desc}
                      </div>
                    </div>
                  )}

                  {/* Checklists do Card */}
                  {activeCard.checklists && activeCard.checklists.length > 0 && (
                    <div className="space-y-1 pt-1.5 border-t border-purple-200">
                      {activeCard.checklists.map((chk) => (
                        <div key={chk.id} className="space-y-1">
                          <span className="text-[0.66rem] font-bold text-purple-900 uppercase">
                            ☑️ {chk.name}:
                          </span>
                          <div className="space-y-1 pl-1">
                            {chk.items.map((item) => (
                              <div key={item.id} className="flex items-center gap-1.5 text-[0.70rem] text-slate-700">
                                <CheckCircle2 size={12} className={item.state === 'complete' ? 'text-emerald-600' : 'text-slate-300'} />
                                <span className={item.state === 'complete' ? 'line-through text-slate-400' : ''}>
                                  {item.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Galeria de Anexos do Card */}
                  {((activeCard.attachments && activeCard.attachments.length > 0) || (activeCard.descImages && activeCard.descImages.length > 0)) && (
                    <div className="ac-audit-attachments-section">
                      <span className="ac-audit-attachments-title">
                        <ImageIcon size={12} className="text-purple-700" />
                        Anexos & Capturas do Card ({((activeCard.attachments?.length || 0) + (activeCard.descImages?.length || 0))})
                      </span>
                      <div className="ac-audit-attachment-grid">
                        {activeCard.attachments?.filter(a => a.isImage).map((att) => (
                          <div
                            key={att.id}
                            className="ac-audit-attachment-thumb"
                            onClick={() => setZoomImageUrl(att.previewUrl || att.url)}
                            title="Clique para ampliar captura"
                          >
                            <img src={att.thumbnailUrl || att.previewUrl || att.url} alt={att.name} loading="lazy" />
                            <span className="ac-audit-attachment-caption">{att.name}</span>
                          </div>
                        ))}
                        {activeCard.descImages?.map((img, idx) => (
                          <div
                            key={`desc_img_${idx}`}
                            className="ac-audit-attachment-thumb"
                            onClick={() => setZoomImageUrl(img.url)}
                            title="Clique para ampliar captura"
                          >
                            <img src={img.url} alt={img.alt} loading="lazy" />
                            <span className="ac-audit-attachment-caption">{img.alt}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center text-slate-400 text-xs">
                  Nenhum card selecionado.
                </div>
              )}
            </div>

            {/* ─── COLUNA 4: DISCUSSÕES DO CARD SELECIONADO (SCROLL INDEPENDENTE) ─── */}
            <div className="ac-audit-col-comments">
              <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                <span className="text-[0.68rem] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                  <MessageSquare size={12} className="text-blue-600" />
                  Discussões do Card ({activeCard?.commentsCount || 0})
                </span>
                <span className="text-[0.60rem] text-slate-400 font-medium">Cronológico</span>
              </div>

              <div className="ac-audit-comments-container">
                {activeCard && activeCard.comments.length > 0 ? (
                  activeCard.comments.map((comm) => (
                    <div key={comm.id} className="ac-audit-comment-card">
                      <div className="ac-audit-comment-header">
                        <div className="ac-audit-comment-author-info">
                          <div className="ac-audit-avatar">
                            {comm.avatarUrl ? (
                              <img src={comm.avatarUrl} alt={comm.author} />
                            ) : (
                              <span>{comm.initials}</span>
                            )}
                          </div>
                          <span className="ac-audit-author-name">{comm.author}</span>
                        </div>
                        <span className="ac-audit-comment-time">
                          {new Date(comm.date).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>

                      <p className="ac-audit-comment-body">{comm.text}</p>

                      {comm.images && comm.images.length > 0 && (
                        <div className="ac-audit-comment-attachments">
                          {comm.images.map((img, idx) => (
                            <div
                              key={idx}
                              className="ac-audit-attachment-thumb"
                              onClick={() => setZoomImageUrl(img.url)}
                              title="Clique para ampliar captura"
                            >
                              <img src={img.url} alt={img.alt} loading="lazy" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center bg-white border border-dashed border-slate-200 rounded-xl my-auto">
                    <MessageSquare size={22} className="mx-auto text-slate-300 mb-1.5" />
                    <p className="text-xs text-slate-500 font-medium">Nenhum comentário registrado neste card.</p>
                  </div>
                )}
              </div>
            </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Lightbox para Zoom em Imagens/Prints em Alta Resolução */}
      {zoomImageUrl && (
        <div
          className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setZoomImageUrl(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] bg-slate-900 p-2 rounded-2xl border border-slate-700 shadow-2xl">
            <button
              onClick={() => setZoomImageUrl(null)}
              className="absolute -top-3 -right-3 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg"
              title="Fechar Visualização"
            >
              <X size={16} />
            </button>
            <img
              src={zoomImageUrl}
              alt="Visualização em Alta Resolução"
              className="max-h-[82vh] max-w-full rounded-xl object-contain"
            />
            <div className="mt-2 text-center">
              <a
                href={zoomImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1 font-mono"
                onClick={(e) => e.stopPropagation()}
              >
                Abrir imagem original em nova aba <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
